"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  BALANCED_EQUATION_STRINGS,
  BalancedEquationCard,
  EquationField,
} from "@/components/tools/chem-reactions/balanced-equation";
import {
  GAS_CONDITIONS_STRINGS,
  GasConditionsFields,
} from "@/components/tools/chem-reactions/gas-conditions";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  AMOUNT_UNITS,
  DEFAULT_GAS_CONDITIONS,
  findAmountUnit,
  type GasConditionsInput,
  molesFrom,
  resolveMolarVolume,
} from "@/lib/tool-kit/chem-reactions/amounts";
import { solveBalanced } from "@/lib/tool-kit/chem-reactions/balance";
import {
  type StoichRow,
  speciesMass,
  stoichiometryRows,
} from "@/lib/tool-kit/chem-reactions/stoichiometry";
import {
  formatSig,
  type NumberFormatter,
  sigFigsOf,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const STOICHIOMETRY_CALCULATOR_STRINGS = {
  ...BALANCED_EQUATION_STRINGS,
  ...GAS_CONDITIONS_STRINGS,
  equationPlaceholder: "N2(g) + H2(g) -> NH3(g)",
  givenSpecies: "Species you know the amount of",
  givenAmount: "Amount",
  givenUnit: "Unit of the amount",
  givenHint: "A mass, an amount in moles, or a volume of gas.",
  results: "Results",
  columnSpecies: "Species",
  columnRole: "Role",
  columnCoefficient: "Coefficient",
  columnMoles: "Moles (mol)",
  columnMass: "Mass (g)",
  columnVolume: "Gas volume (L)",
  roleReactant: "Reactant",
  roleProduct: "Product",
  givenTag: "{role} (given)",
  noValue: "—",
  workingHeading: "The working",
  givenMassLine: "n({name}) = {value} g ÷ {molar} g/mol = {moles} mol",
  givenVolumeLine: "n({name}) = {value} L ÷ {molarVolume} L/mol = {moles} mol",
  givenAmountLine: "n({name}) = {moles} mol",
  ratioLine:
    "n({name}) = {given} mol × ({coefficient} ÷ {givenCoefficient}) = {moles} mol",
  massLine: "m({name}) = {moles} mol × {molar} g/mol = {grams} g",
  volumeLine: "V({name}) = {moles} mol × {molarVolume} L/mol = {litres} L",
  sigNote:
    "Rounded to {count} significant figures, the number in the amount you entered. The coefficients are exact and add none.",
  approxNote:
    "{species} has no IUPAC standard atomic weight, so its mass number is used and its mass is approximate.",
  badAmount: "Enter an amount greater than zero.",
  electronGiven:
    "A mass or volume of electrons is not meaningful here. Pick another species, or give the amount in moles.",
  needsGas:
    "A gas volume needs a valid temperature and pressure. Enter a temperature above absolute zero and a positive pressure.",
  copy: "Copy results",
  copied: "Copied!",
  copyLine: "{name}: {moles} mol, {grams} g",
} as const;

type Strings = typeof STOICHIOMETRY_CALCULATOR_STRINGS;

const EXAMPLES = [
  "N2(g) + H2(g) -> NH3(g)",
  "C3H8(g) + O2(g) -> CO2(g) + H2O(l)",
  "Fe2O3 + CO -> Fe + CO2",
  "CaCO3 -> CaO + CO2(g)",
] as const;
const DEFAULT_SIG = 4;
const GUARD = 1;

interface ResultProps {
  rows: StoichRow[];
  givenMoles: number;
  givenValue: number;
  givenUnit: string;
  molarVolumeValue: number | null;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}

function roleText(row: StoichRow, strings: Strings): string {
  const role =
    row.species.side === "reactant"
      ? strings.roleReactant
      : strings.roleProduct;
  return row.isGiven ? t(strings.givenTag, { role }) : role;
}

function ResultsTable({ rows, sig, strings, fmt }: ResultProps) {
  const showVolume = rows.some((row) => row.litres !== null);
  const cell = (value: number | null) =>
    value === null ? strings.noValue : formatSig(fmt, value, sig);
  return (
    <DataTable
      head={[
        strings.columnSpecies,
        strings.columnRole,
        strings.columnCoefficient,
        strings.columnMoles,
        strings.columnMass,
        ...(showVolume ? [strings.columnVolume] : []),
      ]}
      rows={rows.map((row) => [
        row.species.label,
        roleText(row, strings),
        fmt(row.coefficient),
        cell(row.moles),
        cell(row.grams),
        ...(showVolume ? [cell(row.litres)] : []),
      ])}
    />
  );
}

function givenLine(props: ResultProps, given: StoichRow): string {
  const { strings, fmt, sig, givenValue, givenUnit, givenMoles } = props;
  const kind = findAmountUnit(givenUnit).kind;
  const guard = sig + GUARD;
  const moles = formatSig(fmt, givenMoles, guard);
  if (kind === "mass") {
    return t(strings.givenMassLine, {
      name: given.species.label,
      value: formatSig(
        fmt,
        givenValue * findAmountUnit(givenUnit).factor,
        guard
      ),
      molar: formatSig(fmt, given.molarMass ?? 0, guard),
      moles,
    });
  }
  if (kind === "volume") {
    return t(strings.givenVolumeLine, {
      name: given.species.label,
      value: formatSig(
        fmt,
        givenValue * findAmountUnit(givenUnit).factor,
        guard
      ),
      molarVolume: formatSig(fmt, props.molarVolumeValue ?? 0, guard),
      moles,
    });
  }
  return t(strings.givenAmountLine, { name: given.species.label, moles });
}

function rowLines(props: ResultProps, row: StoichRow, given: StoichRow) {
  const { strings, fmt, sig } = props;
  const guard = sig + GUARD;
  const name = row.species.label;
  const moles = formatSig(fmt, row.moles, guard);
  const lines: string[] = [];
  if (!row.isGiven) {
    lines.push(
      t(strings.ratioLine, {
        name,
        given: formatSig(fmt, props.givenMoles, guard),
        coefficient: fmt(row.coefficient),
        givenCoefficient: fmt(given.coefficient),
        moles,
      })
    );
  }
  if (row.grams !== null && row.molarMass !== null) {
    lines.push(
      t(strings.massLine, {
        name,
        moles,
        molar: formatSig(fmt, row.molarMass, guard),
        grams: formatSig(fmt, row.grams, sig),
      })
    );
  }
  if (row.litres !== null && props.molarVolumeValue !== null) {
    lines.push(
      t(strings.volumeLine, {
        name,
        moles,
        molarVolume: formatSig(fmt, props.molarVolumeValue, guard),
        litres: formatSig(fmt, row.litres, sig),
      })
    );
  }
  return lines;
}

function Working(props: ResultProps) {
  const given = props.rows.find((row) => row.isGiven) as StoichRow;
  const lines = [
    givenLine(props, given),
    ...props.rows.flatMap((row) => rowLines(props, row, given)),
  ];
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{props.strings.workingHeading}</h3>
      <div className={WORKING_BOX}>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
    </div>
  );
}

function Answer(props: ResultProps) {
  const { rows, strings, fmt, sig } = props;
  const approximate = rows
    .filter((row) => row.approximate)
    .map((row) => row.species.label)
    .join(", ");
  const copy = rows
    .map((row) =>
      t(strings.copyLine, {
        name: row.species.label,
        moles: formatSig(fmt, row.moles, sig),
        grams:
          row.grams === null ? strings.noValue : formatSig(fmt, row.grams, sig),
      })
    )
    .join("\n");
  return (
    <>
      <ResultsTable {...props} />
      {approximate === "" ? null : (
        <Notice>{t(strings.approxNote, { species: approximate })}</Notice>
      )}
      <Notice>{t(strings.sigNote, { count: fmt(sig) })}</Notice>
      <Working {...props} />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={copy}
      />
    </>
  );
}

export default function StoichiometryCalculatorWidget() {
  const strings = useToolUi(STOICHIOMETRY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [equation, setEquation] = useState<string>(EXAMPLES[0]);
  const [givenIndex, setGivenIndex] = useState("1");
  const [amount, setAmount] = useState("10.0");
  const [unit, setUnit] = useState("g");
  const [gas, setGas] = useState<GasConditionsInput>(DEFAULT_GAS_CONDITIONS);

  const solved = solveBalanced(equation);
  const species = solved.kind === "ok" ? solved.equation.species : [];
  const index = Math.min(Number(givenIndex), Math.max(species.length - 1, 0));
  const kind = findAmountUnit(unit).kind;
  const hasGas =
    species.some((item) => item.state === "g") || kind === "volume";

  function body() {
    if (solved.kind !== "ok") {
      return null;
    }
    const value = parseMeasuredNumber(amount);
    if (value === null || !(value > 0)) {
      return <Notice>{strings.badAmount}</Notice>;
    }
    const molarVolumeValue = resolveMolarVolume(gas);
    const given = species[index];
    const moles = molesFrom(
      value,
      unit,
      speciesMass(given).total,
      molarVolumeValue
    );
    if (!moles.ok) {
      return (
        <ErrorNotice>
          {moles.reason === "noGas" ? strings.needsGas : strings.electronGiven}
        </ErrorNotice>
      );
    }
    const rows = stoichiometryRows(
      species,
      solved.coefficients,
      index,
      moles.moles,
      molarVolumeValue,
      kind === "volume" ? index : null
    );
    return (
      <Answer
        fmt={fmt}
        givenMoles={moles.moles}
        givenUnit={unit}
        givenValue={value}
        molarVolumeValue={molarVolumeValue}
        rows={rows}
        sig={sigFigsOf(amount) ?? DEFAULT_SIG}
        strings={strings}
      />
    );
  }

  return (
    <>
      <EquationField
        examples={EXAMPLES}
        id="stoich-equation"
        onChange={setEquation}
        strings={strings}
        value={equation}
      />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <BalancedEquationCard solved={solved} strings={strings} />
        {solved.kind === "ok" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="stoich-given"
              label={strings.givenSpecies}
              onChange={setGivenIndex}
              options={species.map((item, position) => ({
                value: String(position),
                label: item.label,
              }))}
              value={String(index)}
            />
            <QuantityInput
              hint={strings.givenHint}
              id="stoich-amount"
              label={strings.givenAmount}
              onUnitChange={setUnit}
              onValueChange={setAmount}
              unit={unit}
              unitLabel={strings.givenUnit}
              units={AMOUNT_UNITS.map((u) => ({
                value: u.id,
                label: u.symbol,
              }))}
              value={amount}
            />
          </div>
        ) : null}
        {hasGas && solved.kind === "ok" ? (
          <GasConditionsFields
            fmt={fmt}
            id="stoich-gas"
            onChange={setGas}
            strings={strings}
            value={gas}
          />
        ) : null}
        {body()}
      </section>
    </>
  );
}
